import { useEffect, useRef } from 'react';

/**
 * Auto-logout on inactivity + keep-alive while active.
 *
 *  - Tracks user activity (mouse/keyboard/scroll/touch/click).
 *  - Every minute: if idle for `minutes`+, calls `onIdle('inactivity')`.
 *  - Every `keepaliveEveryMs` while user is active, calls /api/auth/keepalive
 *    so the server-side session is refreshed (otherwise it expires while
 *    they're browsing without triggering other API calls).
 *  - Polls /api/auth/me every minute to detect server-side expiry early.
 *
 *  Usage:
 *    useIdleLogout({ minutes: 30, onIdle: handleLogout });
 */
export function useIdleLogout({ minutes = 30, onIdle, keepaliveEveryMs = 5 * 60 * 1000 }) {
  const timerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const lastKeepaliveRef = useRef(Date.now());

  useEffect(() => {
    const limitMs = minutes * 60 * 1000;

    const reset = () => {
      lastActivityRef.current = Date.now();
    };

    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));

    // Tick every minute
    timerRef.current = setInterval(async () => {
      const now = Date.now();
      const idleMs = now - lastActivityRef.current;

      if (idleMs >= limitMs) {
        onIdle?.('inactivity');
        return;
      }

      // User has been active recently — refresh the server session if it's
      // been a while since the last keepalive
      if (now - lastKeepaliveRef.current >= keepaliveEveryMs) {
        try {
          const r = await fetch('/api/auth/keepalive', { method: 'POST', credentials: 'include' });
          if (r.ok) {
            lastKeepaliveRef.current = now;
          } else if (r.status === 401) {
            onIdle?.('expired');
            return;
          }
        } catch { /* ignore network glitch */ }
      } else {
        // Lightweight expiry check (doesn't refresh the cookie)
        try {
          const r = await fetch('/api/auth/me', { credentials: 'include' });
          if (r.status === 401) {
            onIdle?.('expired');
          }
        } catch { /* ignore */ }
      }
    }, 60_000);

    return () => {
      events.forEach((e) => window.removeEventListener(e, reset));
      clearInterval(timerRef.current);
    };
  }, [minutes, onIdle, keepaliveEveryMs]);
}
